/**
 * Signup Screen - Glassmorphic Redesign
 *
 * Features:
 * - Dark glassmorphic background (#1a1a2e)
 * - GlassCard container with chrome metallic border
 * - GlassButton (theatrical variant) for CTAs
 * - GlassPill brand header
 * - Glassmorphic input fields
 * - Tier-adaptive typography
 * - Full i18n support
 * - WCAG AA accessibility
 *
 * Updated: 2025-02-05 (Glassmorphic Redesign - Phase 1-3)
 */
import { View, Text, TextInput, Alert, Platform, ScrollView, Pressable, StyleSheet } from "react-native";
// Use keyboard-controller's KeyboardAvoidingView for reliable behavior across all devices
import { KeyboardAvoidingView } from "react-native-keyboard-controller";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useTranslation } from "@dramadetector/shared";
import { TierHeadline, TierBody } from "@dramadetector/ui";

import { useAuth } from "../../contexts/AuthContext";
import { signupSchema, type SignupFormData } from "../../lib/auth-validation";
import { useMobileWebKeyboard } from "../../hooks/useMobileWebKeyboard";
import { GoogleSignInButton } from "../../components/GoogleSignInButton";
import { Logo } from "../../components/Logo";
import { GlassButton } from "../../components/glass/GlassButton";
import { GlassPill } from "../../components/glass/GlassPill";
import { debugLog } from "../../lib/debug-logger";
import { captureEvent } from "../../lib/analytics";
import type { AuthEventProperties } from "../../lib/analytics/events";

// Sources tagged by AnonymousQuotaCounter when navigating to signup with
// `?from=anon-cta&source=...`. Type is derived from the canonical definition
// in lib/analytics/events/auth.ts → Auth_AnonCTA_Clicked.
type AnonCtaSource = AuthEventProperties['Auth_AnonCTA_Clicked']['source'];
const ANON_CTA_SOURCES = new Set<AnonCtaSource>([
  'home-quota-counter',
  'quota-exhausted-counter',
]);

export default function SignupScreen() {
  const { t } = useTranslation('auth');
  const router = useRouter();
  const params = useLocalSearchParams<{ from?: string; source?: string }>();
  const insets = useSafeAreaInsets();
  const { signUp } = useAuth();
  const [loading, setLoading] = useState(false);

  // AUTH-UX-C (NocoDB #3908): fire the source side of the anon→authed
  // funnel exactly once per signup-screen mount when arriving from an
  // anon-CTA. Pairs with Auth_AnonAccountLinked (sink, fired by
  // AuthContext post-signup once `posthog.alias()` links the profiles).
  // Ref guard prevents double-fire under React 18 StrictMode dev double-mount.
  const anonCtaTracked = useRef(false);
  useEffect(() => {
    if (anonCtaTracked.current) return;
    if (params.from !== 'anon-cta') return;
    const source = params.source;
    if (!source || !ANON_CTA_SOURCES.has(source as AnonCtaSource)) return;
    captureEvent('Auth_AnonCTA_Clicked', { source: source as AnonCtaSource });
    anonCtaTracked.current = true;
  }, [params.from, params.source]);

  // Mobile web keyboard fix: Scroll inputs into view when virtual keyboard appears
  useMobileWebKeyboard();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  // Bot protection note: Turnstile was removed — token was never validated server-side.
  // If auth abuse becomes a problem, use Supabase Auth's native captcha support:
  // signUp({ captchaToken }) — validates server-side without custom edge functions.
  // See: https://supabase.com/docs/guides/auth/auth-captcha
  const onSubmit = async (data: SignupFormData) => {
    try {
      debugLog('event', 'Signup form submitted', { email: data.email });
      setLoading(true);

      debugLog('event', 'Calling signUp API');
      const { error } = await signUp(data.email, data.password);

      if (error) {
        console.error('[Signup] Signup failed:', error.message);
        if (Platform.OS === 'web') {
          window.alert(`${t('signup.errors.signup_failed')}\n\n${error.message}`);
        } else {
          Alert.alert(t('signup.errors.signup_failed'), error.message);
        }
        return;
      }

      debugLog('event', 'Signup successful');

      // Platform-specific success handling
      if (Platform.OS === 'web') {
        // Web: Use browser confirm dialog (works reliably)
        if (window.confirm(`${t('signup.success.title')}\n\n${t('signup.success.message')}`)) {
          router.replace("/(auth)/login");
        } else {
          router.replace("/(auth)/login"); // Navigate anyway
        }
      } else {
        // Mobile: Use React Native Alert
        Alert.alert(
          t('signup.success.title'),
          t('signup.success.message'),
          [{ text: 'OK', onPress: () => router.replace("/(auth)/login") }]
        );
      }
    } catch (error) {
      console.error('[Signup] Unexpected error:', error);
      if (Platform.OS === 'web') {
        window.alert(`Error\n\n${t('signup.errors.unexpected_error')}`);
      } else {
        Alert.alert('Error', t('signup.errors.unexpected_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate bottom padding for iOS home indicator (no tab bar on auth screens)
  const bottomPadding = Math.max(insets.bottom, 24);

  return (
    <KeyboardAvoidingView
      behavior="padding"
      style={styles.container}
    >
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingHorizontal: 24, paddingBottom: bottomPadding }}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.contentContainer}>
          {/* Logo - Brand Consistency */}
          <View style={styles.logoContainer}>
            <Logo size="lg" style={{ marginBottom: 16 }} />
            <GlassPill translationKey="app_name" namespace="common" size="lg" />
          </View>

          {/* Form Content - Direct on dark background (matches home screen) */}
          <View style={styles.formContainer}>
            {/* Header - Tier-Adaptive Typography */}
            <View style={styles.headerContainer}>
              <TierHeadline size="3xl" weight="bold" style={styles.title}>
                {t('signup.title')}
              </TierHeadline>
              <TierBody size="lg" style={styles.subtitle}>
                {t('signup.subtitle')}
              </TierBody>
            </View>

            {/* Google Sign-in */}
            <GoogleSignInButton disabled={loading} />

            {/* Divider */}
            <View style={styles.dividerContainer}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>{t('signup.divider')}</Text>
              <View style={styles.dividerLine} />
            </View>

            {/* Email Field */}
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>
                {t('signup.email_label')}
              </Text>
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={[styles.input, errors.email && styles.inputError]}
                    placeholder={t('signup.email_placeholder')}
                    placeholderTextColor="rgba(255, 255, 255, 0.4)"
                    autoCapitalize="none"
                    autoCorrect={false}
                    keyboardType="email-address"
                    textContentType="emailAddress"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    editable={!loading}
                    accessibilityLabel={t('signup.accessibility.email_address')}
                    accessibilityHint={t('signup.accessibility.email_hint')}
                  />
                )}
              />
              {errors.email && (
                <Text style={styles.errorText}>
                  {errors.email.message}
                </Text>
              )}
            </View>

            {/* Password Field */}
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>
                {t('signup.password_label')}
              </Text>
              <Controller
                control={control}
                name="password"
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={[styles.input, errors.password && styles.inputError]}
                    placeholder={t('signup.password_placeholder')}
                    placeholderTextColor="rgba(255, 255, 255, 0.4)"
                    secureTextEntry
                    textContentType="newPassword"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    editable={!loading}
                    accessibilityLabel={t('signup.accessibility.password')}
                    accessibilityHint={t('signup.accessibility.password_hint')}
                  />
                )}
              />
              {errors.password && (
                <Text style={styles.errorText}>
                  {errors.password.message}
                </Text>
              )}
            </View>

            {/* Confirm Password Field */}
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>
                {t('signup.confirm_password_label')}
              </Text>
              <Controller
                control={control}
                name="confirmPassword"
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={[styles.input, errors.confirmPassword && styles.inputError]}
                    placeholder={t('signup.confirm_password_placeholder')}
                    placeholderTextColor="rgba(255, 255, 255, 0.4)"
                    secureTextEntry
                    textContentType="newPassword"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    editable={!loading}
                    accessibilityLabel={t('signup.accessibility.confirm_password')}
                    accessibilityHint={t('signup.accessibility.confirm_password_hint')}
                  />
                )}
              />
              {errors.confirmPassword && (
                <Text style={styles.errorText}>
                  {errors.confirmPassword.message}
                </Text>
              )}
            </View>

            {/* Submit Button - Theatrical Variant */}
            <GlassButton
              variant="theatrical"
              size="normal"
              onPress={() => {
                debugLog('button', 'Signup button pressed with errors', { errors });
                debugLog('state', 'Signup form values', { email: control._formValues.email });
                handleSubmit(onSubmit)();
              }}
              disabled={loading}
              style={styles.submitButton}
              showIcon
            >
              {loading ? t('signup.creating_account') : t('signup.sign_up_button')}
            </GlassButton>

            {/* Login Link */}
            <View style={styles.linkContainer}>
              <Text style={styles.linkText}>
                {t('signup.already_have_account')}{" "}
              </Text>
              <Pressable
                onPress={() => router.push("/(auth)/login")}
                accessibilityRole="link"
                accessibilityLabel={t('signup.accessibility.sign_in')}
              >
                <Text style={styles.linkTextHighlight}>
                  {t('signup.sign_in_link')}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1a1a2e', // Dark glassmorphic background
  },
  contentContainer: {
    width: '100%',
    maxWidth: 480,
    marginHorizontal: 'auto',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  formContainer: {
    // No container styling needed - let form breathe directly on dark background
  },
  headerContainer: {
    marginBottom: 24,
    alignItems: 'center',
  },
  title: {
    textAlign: 'center',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  subtitle: {
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.7)',
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
  },
  dividerText: {
    marginHorizontal: 16,
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.5)',
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 8,
  },
  input: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#FFFFFF',
    // Glassmorphic subtle shadow
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  inputError: {
    borderColor: 'rgba(239, 68, 68, 0.6)', // Red with transparency
  },
  errorText: {
    fontSize: 14,
    color: '#EF4444',
    marginTop: 6,
  },
  submitButton: {
    marginBottom: 16,
  },
  linkContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  linkText: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  linkTextHighlight: {
    fontSize: 14,
    color: '#EC4899', // Pink accent (theatrical gradient primary)
    fontWeight: '600',
  },
});
